// PrintService web component
import { AwardStudentRow, AwardSubjectResult } from './awardTypes';
import { SubjectGroup, PrintSubject } from './subjectGroupTypes';

// ─────────────────────────────────────────────────────────────────────────────
// PrintService
//
// Ported from the SkoolPillar (Expo/React Native) app's PrintService.ts.
// Same concepts, web equivalents: localStorage instead of AsyncStorage,
// window.open()+print() instead of expo-print.
//
// Covers: subject-group/exclusion persistence, subject-column derivation,
// grade/percentage math, and the HTML builders for the class result sheet
// and individual student report card.
// ─────────────────────────────────────────────────────────────────────────────

export type Orientation = 'portrait' | 'landscape';
export type PaperSize = 'A4' | 'A3';
export type ColorMode = 'color' | 'bw';

export interface PrintDisplayOptions {
  showPercentage: boolean;
  calculatePercentage: boolean;
  showGrade: boolean;
  colorMode: ColorMode;
}

// ── localStorage persistence (per class) ─────────────────────────────────────

const groupsKey = (classId: string) => `award_subject_groups_${classId}`;
const exclusionsKey = (classId: string) => `award_subject_exclusions_${classId}`;

export const loadSubjectGroups = (classId: string): SubjectGroup[] => {
  try {
    const raw = localStorage.getItem(groupsKey(classId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveSubjectGroups = (classId: string, groups: SubjectGroup[]): void => {
  try {
    localStorage.setItem(groupsKey(classId), JSON.stringify(groups));
  } catch {
    // best-effort cache; ignore failures
  }
};

export const loadSubjectExclusions = (classId: string): number[] => {
  try {
    const raw = localStorage.getItem(exclusionsKey(classId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveSubjectExclusions = (classId: string, ids: number[]): void => {
  try {
    localStorage.setItem(exclusionsKey(classId), JSON.stringify(ids));
  } catch {
    // best-effort cache; ignore failures
  }
};

// ── Group / exclusion helpers ─────────────────────────────────────────────

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

// synthetic subject_id used for a combined column — guaranteed not to collide
// with real subject ids (which are always positive).
const groupSyntheticId = (g: SubjectGroup): number => -1 - Math.abs(hashString(g.id));

/** Unique subject list gathered across every student row, in first-seen order. */
export const getAllSubjects = (rows: AwardStudentRow[]): PrintSubject[] => {
  const map = new Map<number, string>();
  rows.forEach((r) => {
    r.subjects.forEach((s) => {
      if (!map.has(s.subject_id)) map.set(s.subject_id, s.subject_name);
    });
  });
  return Array.from(map.entries()).map(([subject_id, subject_name]) => ({ subject_id, subject_name }));
};

/**
 * Sorts student rows by roll number ascending (numeric where possible,
 * falling back to string compare). Used so every printout — class result
 * sheets and report cards alike — lines students up in roll-no order,
 * regardless of the order (e.g. rank order) they arrive in from the API.
 */
const sortByRollNo = (rows: AwardStudentRow[]): AwardStudentRow[] => {
  return [...rows].sort((a, b) => {
    const rollA = Number(a.student_roll_no);
    const rollB = Number(b.student_roll_no);
    if (!Number.isNaN(rollA) && !Number.isNaN(rollB)) return rollA - rollB;
    return String(a.student_roll_no).localeCompare(String(b.student_roll_no));
  });
};

/**
 * Given the real subject list + groups, returns the subject columns to
 * actually render: ungrouped subjects untouched, plus one synthetic subject
 * per group (in the position of its first member), grouped subjects removed.
 */
export const buildDisplaySubjects = (subjects: PrintSubject[], groups: SubjectGroup[]): PrintSubject[] => {
  const activeGroups = groups.filter((g) => g.subjectIds.length > 0 && g.name.trim());
  if (activeGroups.length === 0) return subjects;

  const memberToGroup = new Map<number, SubjectGroup>();
  activeGroups.forEach((g) => g.subjectIds.forEach((sid) => memberToGroup.set(sid, g)));

  const result: PrintSubject[] = [];
  const placedGroupIds = new Set<string>();

  subjects.forEach((sub) => {
    const group = memberToGroup.get(sub.subject_id);
    if (!group) {
      result.push(sub);
      return;
    }
    if (placedGroupIds.has(group.id)) return;
    placedGroupIds.add(group.id);
    result.push({ subject_id: groupSyntheticId(group), subject_name: group.name.trim() });
  });

  return result;
};

export const applyExclusionsToSubjects = (subjects: PrintSubject[], excludedIds: number[]): PrintSubject[] => {
  if (excludedIds.length === 0) return subjects;
  const excludedSet = new Set(excludedIds);
  return subjects.filter((s) => !excludedSet.has(s.subject_id));
};

/** Merges a group's member subject results into one synthetic AwardSubjectResult. */
const mergeSubjectResults = (members: AwardSubjectResult[], groupId: number, groupName: string): AwardSubjectResult => {
  const anyHasMarks = members.some((m) => m.has_marks);
  const allNumeric = members.every((m) => !m.has_marks || m.evaluation_type === 'marks');

  if (allNumeric) {
    const obtained = members.reduce((s, m) => s + (m.has_marks ? m.obtained_marks ?? 0 : 0), 0);
    const max = members.reduce((s, m) => s + (m.max_marks ?? 0), 0);
    return {
      subject_id: groupId,
      subject_name: groupName,
      has_marks: anyHasMarks,
      max_marks: max || null,
      evaluation_type: 'marks',
      obtained_marks: anyHasMarks ? obtained : null,
      obtained_grade: null,
    };
  }

  const grades = members.filter((m) => m.has_marks).map((m) => m.obtained_grade ?? '—').join(', ');
  return {
    subject_id: groupId,
    subject_name: groupName,
    has_marks: anyHasMarks,
    max_marks: null,
    evaluation_type: members.find((m) => m.has_marks)?.evaluation_type ?? null,
    obtained_marks: null,
    obtained_grade: anyHasMarks ? grades : null,
  };
};

/** Applies subject groups to one row's per-subject breakdown. */
const applyGroupsToRowSubjects = (subjects: AwardSubjectResult[], groups: SubjectGroup[]): AwardSubjectResult[] => {
  const activeGroups = groups.filter((g) => g.subjectIds.length > 0 && g.name.trim());
  if (activeGroups.length === 0) return subjects;

  const memberIds = new Set(activeGroups.flatMap((g) => g.subjectIds));
  const result: AwardSubjectResult[] = subjects.filter((s) => !memberIds.has(s.subject_id));

  activeGroups.forEach((g) => {
    const members = subjects.filter((s) => g.subjectIds.includes(s.subject_id));
    if (members.length === 0) return;
    result.push(mergeSubjectResults(members, groupSyntheticId(g), g.name.trim()));
  });

  return result;
};

const applyExclusionsToRowSubjects = (subjects: AwardSubjectResult[], excludedIds: number[]): AwardSubjectResult[] => {
  if (excludedIds.length === 0) return subjects;
  const excludedSet = new Set(excludedIds);
  return subjects.filter((s) => !excludedSet.has(s.subject_id));
};

// ── Display / grade helpers ────────────────────────────────────────────────

const displayValueForSubject = (s: AwardSubjectResult | undefined): string => {
  if (!s || !s.has_marks) return '—';
  if (s.evaluation_type === 'marks') {
    return s.max_marks ? `${s.obtained_marks ?? 0}/${s.max_marks}` : `${s.obtained_marks ?? 0}`;
  }
  return s.obtained_grade ?? '—';
};

const subjectPercent = (s: AwardSubjectResult | undefined): number | null => {
  if (!s || !s.has_marks || s.evaluation_type !== 'marks' || !s.max_marks) return null;
  return ((s.obtained_marks ?? 0) / s.max_marks) * 100;
};

const gradeFromPercent = (pct: number | null): string => {
  if (pct === null) return '—';
  if (pct >= 90) return 'A+';
  if (pct >= 80) return 'A';
  if (pct >= 70) return 'B+';
  if (pct >= 60) return 'B';
  if (pct >= 50) return 'C+';
  if (pct >= 40) return 'C';
  if (pct >= 33) return 'D';
  return 'F';
};

const printPercentColor = (pct: number | null): string => {
  if (pct === null) return '#9CA3AF';
  if (pct >= 75) return '#16A34A';
  if (pct >= 40) return '#D97706';
  return '#DC2626';
};

/**
 * Formats a percentage value for display. When calculation is turned off
 * (the "Calculate %" toggle in the print modal), this always returns a
 * blank dash — even if a real pct value was computed upstream — so the
 * printed column is genuinely empty for hand-filling rather than silently
 * showing computed numbers the user asked to suppress.
 */
const formatPct = (pct: number | null, calculate: boolean): string =>
  calculate && pct !== null ? `${pct.toFixed(1)}%` : '—';

// ── Color-mode palette ──────────────────────────────────────────────────────
// Every banner/header/table-head color in the document is driven from this
// single palette. 'color' reproduces the original blue-gradient design
// pixel-for-pixel; 'bw' is a genuine white-bg/black-text/black-border print
// style — not just a flattened, still-blue version of the same design.

interface Palette {
  bandBg: string;      // header / student-banner background
  bandBorder: string;  // header / student-banner border
  textPrimary: string; // headline text sitting on the band
  textSecondary: string; // timestamps / subtext sitting on the band
  textMuted: string;   // faint labels sitting on the band
  chipBg: string;      // small pill/box backgrounds on the band (logo box, date chip, avatar placeholder)
  chipBorder: string;
  dividerColor: string;   // thin rule inside the band + column dividers in thead
  theadBg: string;
  theadText: string;
  theadBottomBorder: string; // separates thead visually from tbody (only needed when bg is white)
  tableBorder: string;
  tableShadow: string;
}

const getPalette = (colorMode: ColorMode): Palette => {
  if (colorMode === 'bw') {
    return {
      bandBg: '#FFFFFF',
      bandBorder: '2px solid #111827',
      textPrimary: '#111827',
      textSecondary: '#4B5563',
      textMuted: '#6B7280',
      chipBg: '#F3F4F6',
      chipBorder: '1px solid #9CA3AF',
      dividerColor: '#9CA3AF',
      theadBg: '#FFFFFF',
      theadText: '#111827',
      theadBottomBorder: '2px solid #111827',
      tableBorder: '#898B8D',
      tableShadow: 'none',
    };
  }
  return {
    bandBg: 'linear-gradient(135deg,#1e40af 0%,#3b82f6 55%,#60a5fa 100%)',
    bandBorder: 'none',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255,255,255,0.65)',
    textMuted: 'rgba(255,255,255,0.6)',
    chipBg: 'rgba(255,255,255,0.18)',
    chipBorder: '1px solid rgba(255,255,255,0.25)',
    dividerColor: 'rgba(255,255,255,0.18)',
    theadBg: 'linear-gradient(135deg,#1e40af,#3b82f6)',
    theadText: 'rgba(255,255,255,0.85)',
    theadBottomBorder: 'none',
    tableBorder: '#E5E7EB',
    tableShadow: '0 2px 10px rgba(0,0,0,0.06)',
  };
};

// In B&W mode, colored badges/text are neutralized to grayscale-safe tones so
// nothing relies on a color channel a black-ink-only printer would drop.

const BW_INK = '#1F2937';    // neutral dark slate for text/icons
const BW_BORDER = '#6B7280'; // neutral mid-gray for badge borders

/** Text/icon color: original hex in color mode, neutral dark gray in B&W. */
const ink = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? BW_INK : hex);

/** Badge fill: tinted background in color mode, transparent (outline-only) in B&W. */
const inkBg = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? 'transparent' : `${hex}18`);

/** Badge border: tinted border in color mode, solid neutral gray in B&W. */
const inkBorder = (hex: string, colorMode: ColorMode): string => (colorMode === 'bw' ? BW_BORDER : `${hex}40`);

/** Pass/Fail status badge — in B&W it drops the green/red tint entirely and relies on the ✅/❌ + text. */
const statusBadge = (passed: boolean, colorMode: ColorMode) => {
  if (colorMode === 'bw') {
    return { bg: 'transparent', border: '1px solid #111827', text: '#111827' };
  }
  return passed
    ? { bg: 'rgba(16,185,129,0.25)', border: '1px solid rgba(16,185,129,0.4)', text: '#6EE7B7' }
    : { bg: 'rgba(239,68,68,0.25)', border: '1px solid rgba(239,68,68,0.4)', text: '#FCA5A5' };
};

/** Sums the numeric ("marks"-type) entries of a row's (already grouped+excluded) subjects. */
const sumRowMarks = (subjects: AwardSubjectResult[]): { obt: number; max: number; pct: number | null } => {
  let obt = 0;
  let max = 0;
  subjects.forEach((s) => {
    if (s.has_marks && s.evaluation_type === 'marks') {
      obt += s.obtained_marks ?? 0;
      max += s.max_marks ?? 0;
    }
  });
  return { obt, max, pct: max > 0 ? (obt / max) * 100 : null };
};

const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const fmtDateTime = () =>
  new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });

// ── Shared print CSS ─────────────────────────────────────────────────────

/**
 * Base stylesheet, parameterized by colorMode:
 *  - table borders darken from #E5E7EB to #898B8D in B&W (prints cleanly as ink)
 *  - table-wrap drops its soft box-shadow in B&W (shadows don't translate to print)
 *  - header/thead swap their blue gradient for a genuine white/black-border
 *    treatment in B&W (not just a flattened navy block)
 */
const getBaseStyles = (colorMode: ColorMode): string => {
  const p = getPalette(colorMode);

  return `
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
     background:#F0F4FB;color:#0F1B3D;font-size:13px;
     -webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{max-width:1400px;margin:0 auto;padding:24px}
@page{size:landscape;margin:10mm}
.header{background:${p.bandBg};border:${p.bandBorder};
        border-radius:16px;padding:22px 26px;margin-bottom:20px;
        display:flex;justify-content:space-between;align-items:flex-start}
.hl{flex:1}.hr{text-align:right;flex-shrink:0;margin-left:20px}
.hr .cnt{font-size:32px;font-weight:900;color:${p.textPrimary};line-height:1}
.hr .lbl{font-size:11px;color:${p.textSecondary};margin-top:2px}
.logo-row{display:flex;align-items:center;gap:12px;margin-bottom:10px}
.logo-box{width:44px;height:44px;background:${p.chipBg};border-radius:12px;
          display:flex;align-items:center;justify-content:center;font-size:24px;
          border:${p.chipBorder}}
.logo-name{font-size:22px;font-weight:900;color:${p.textPrimary};letter-spacing:-0.8px;line-height:1.1}
.logo-sub{font-size:9px;color:${p.textMuted};font-weight:700;letter-spacing:1.2px;text-transform:uppercase;margin-top:2px}
.divider{height:1px;background:${p.dividerColor};margin:10px 0}
.table-wrap{background:#fff;border-radius:14px;overflow:hidden;border:1px solid ${p.tableBorder};
            box-shadow:${p.tableShadow};margin-bottom:22px}
table{width:100%;border-collapse:collapse}
thead tr{background:${p.theadBg};border-bottom:${p.theadBottomBorder}}
thead th{padding:12px 10px;text-align:left;font-size:10px;font-weight:700;
         color:${p.theadText};letter-spacing:0.6px;text-transform:uppercase;white-space:nowrap;
         border-right:1px solid ${p.dividerColor}}
thead th:last-child{border-right:none}
thead th.c{text-align:center}
tbody td{padding:10px;border-bottom:1px solid ${p.tableBorder};border-right:1px solid ${p.tableBorder};
         vertical-align:middle;text-align:left}
tbody td.c{text-align:center}
tbody td:last-child{border-right:none}
tbody tr:last-child td{border-bottom:none}
.footer{text-align:center;color:#94A3B8;font-size:11px;padding:6px 0 10px}
thead{display:table-header-group}
tbody tr{page-break-inside:avoid;break-inside:avoid}
@media print{
  body{background:#fff}.page{padding:10px;max-width:100%}
  tbody tr{page-break-inside:avoid;break-inside:avoid}
}`;
};

/**
 * Header logo block. Renders the real school logo (grayscaled automatically
 * in B&W print mode) when a logoUrl is available, falling back to the
 * placeholder emoji + "SkoolPillar / ERP Management System" copy otherwise —
 * same graceful-degradation pattern TopBar.tsx uses for the school name/logo.
 */
const buildLogoHtml = (
  schoolName: string | undefined,
  logoUrl: string | undefined,
  colorMode: ColorMode,
): string => {
  const grayscaleFilter = colorMode === 'bw' ? 'filter:grayscale(1);' : '';
  const logoInner = logoUrl
    ? `<img src="${logoUrl}" alt="School logo" style="width:100%;height:100%;object-fit:contain;border-radius:10px;${grayscaleFilter}" />`
    : '&#127979;';

  return `
<div class="logo-row">
  <div class="logo-box" style="${logoUrl ? 'background:#fff;padding:4px;' : ''}">${logoInner}</div>
  <div>
    <div class="logo-name">${schoolName ? escapeHtml(schoolName) : 'SkoolPillar'}</div>
    <div class="logo-sub">${schoolName ? 'Powered by SkoolPillar ERP' : 'ERP Management System'}</div>
  </div>
</div>
<div class="divider"></div>`;
};

const AVATAR_ACCENTS = [
  { bg: '#EFF4FF', color: '#2855AE' },
  { bg: '#E6F9F7', color: '#00A896' },
  { bg: '#FDF2F8', color: '#EC4899' },
  { bg: '#FFF7ED', color: '#F59E0B' },
  { bg: '#FEF2F2', color: '#EF4444' },
  { bg: '#ECFDF5', color: '#10B981' },
];

const BW_AVATAR_ACCENT = { bg: '#F3F4F6', color: BW_INK };

/** Overrides the @page rule with the chosen paper size + orientation. */
export const applyPrintOptions = (html: string, paper: PaperSize, orientation: Orientation): string => {
  const override = `<style>@page{size:${paper} ${orientation};margin:10mm}</style>`;
  return html.includes('</head>') ? html.replace('</head>', `${override}</head>`) : override + html;
};

/** Opens a new tab with the given HTML and triggers the browser's print dialog. */
export const printHtml = (html: string): void => {
  const win = window.open('', '_blank');
  if (!win) {
    window.alert('Please allow pop-ups for this site to print.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 350);
};

// ── HTML builders ──────────────────────────────────────────────────────────

export const buildClassResultHtml = (
  rows: AwardStudentRow[],
  displaySubjects: PrintSubject[],
  groups: SubjectGroup[],
  excludedIds: number[],
  className: string,
  section: string | null,
  examLabel: string,
  schoolName: string | undefined,
  schoolLogoUrl: string | undefined,
  displayOptions: PrintDisplayOptions,
): string => {
  const { showPercentage, calculatePercentage, showGrade, colorMode } = displayOptions;
  const p = getPalette(colorMode);
  const now = fmtDateTime();

  // Always print in roll-no order, regardless of the order rows arrived in
  // (the API returns them rank-sorted for the on-screen merit list).
  const sortedRows = sortByRollNo(rows);

  const rowsHtml = sortedRows.map((row, i) => {
    const subs = applyExclusionsToRowSubjects(applyGroupsToRowSubjects(row.subjects, groups), excludedIds);
    const subMap = new Map(subs.map((s) => [s.subject_id, s]));
    const accent = colorMode === 'bw' ? BW_AVATAR_ACCENT : AVATAR_ACCENTS[i % AVATAR_ACCENTS.length];
    const initials = getInitials(row.student_name);

    const avatarHtml = row.student_profile_picture
      ? `<img src="${row.student_profile_picture}" width="34" height="34" style="border-radius:8px;object-fit:cover;display:inline-block;border:1.5px solid #E5E7EB;vertical-align:middle;flex-shrink:0;" />`
      : `<div style="display:inline-flex;width:34px;height:34px;min-width:34px;border-radius:8px;background:${accent.bg};border:1.5px solid ${accent.color}30;align-items:center;justify-content:center;font-size:12px;font-weight:800;color:${accent.color};vertical-align:middle;flex-shrink:0;">${initials}</div>`;

    const { pct: overallPct } = sumRowMarks(subs);
    const overallGrade = gradeFromPercent(overallPct);
    const gc = printPercentColor(overallPct);

    const subjectCells = displaySubjects.map((sub) => {
      const s = subMap.get(sub.subject_id);
      const val = displayValueForSubject(s);
      const pct = subjectPercent(s);
      const rawColor = val === '—' ? '#9CA3AF' : pct === null ? '#2855AE' : printPercentColor(pct);
      const color = val === '—' ? rawColor : ink(rawColor, colorMode);
      return `<td class="c"><span style="font-weight:700;color:${color};font-size:11px;">${val}</span></td>`;
    }).join('');

    return `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#F8FAFF'};">
        <td class="c" style="color:#94A3B8;font-size:11px;font-weight:700;">${i + 1}</td>
        <td class="c" style="color:${ink('#2855AE', colorMode)};font-weight:800;font-size:12px;">${row.student_roll_no ?? '—'}</td>
        <td style="padding:8px 10px;">
          <div style="display:flex;align-items:center;gap:10px;">
            ${avatarHtml}
            <span style="font-weight:700;color:#0F1B3D;font-size:12px;">${escapeHtml(row.student_name)}</span>
          </div>
        </td>
        ${subjectCells}
        ${showPercentage
          ? `<td class="c">${calculatePercentage && overallPct !== null
              ? `<span style="font-weight:800;color:${ink(gc, colorMode)};font-size:12px;">${overallPct.toFixed(1)}%</span>`
              : `<span style="color:#9CA3AF;">—</span>`}</td>`
          : ''}
        ${showGrade ? `<td class="c"><span style="background:${inkBg(gc, colorMode)};color:${ink(gc, colorMode)};border:1px solid ${inkBorder(gc, colorMode)};padding:3px 10px;border-radius:12px;font-size:12px;font-weight:800;">${overallGrade}</span></td>` : ''}
      </tr>`;
  }).join('');

  const title = `Result Sheet · Class ${className}${section ? ` · ${section}` : ''}${examLabel ? ` · ${examLabel}` : ''}`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>${title}</title><style>${getBaseStyles(colorMode)}</style></head>
<body><div class="page">
  <div class="header">
    <div class="hl">
      ${buildLogoHtml(schoolName, schoolLogoUrl, colorMode)}
      <div style="font-size:15px;font-weight:800;color:${p.textPrimary};letter-spacing:-0.2px;">${title}</div>
      <div style="font-size:11px;color:${p.textSecondary};margin-top:4px;">Generated: ${now}</div>
    </div>
    <div class="hr">
      <div class="cnt">${sortedRows.length}</div>
      <div class="lbl">Students</div>
      <div class="lbl" style="margin-top:4px;">${displaySubjects.length} Subject${displaySubjects.length !== 1 ? 's' : ''}</div>
    </div>
  </div>
  <div class="table-wrap">
    <table>
      <thead><tr>
        <th class="c" style="width:36px;">#</th>
        <th class="c" style="width:54px;">Roll</th>
        <th>Student</th>
        ${displaySubjects.map((s) => `<th class="c">${escapeHtml(s.subject_name)}</th>`).join('')}
        ${showPercentage ? `<th class="c">Overall %</th>` : ''}
        ${showGrade ? `<th class="c">Grade</th>` : ''}
      </tr></thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="${displaySubjects.length + 3 + (showPercentage ? 1 : 0) + (showGrade ? 1 : 0)}" style="text-align:center;padding:40px;color:#94A3B8;">No students found</td></tr>`}
      </tbody>
    </table>
  </div>
  <div class="footer"><span style="font-weight:700;color:#2855AE;">${schoolName ? escapeHtml(schoolName) : 'SkoolPillar ERP'}</span> &nbsp;·&nbsp; ${schoolName ? 'Powered by SkoolPillar ERP' : 'School Management System'} &nbsp;·&nbsp; ${now}</div>
</div></body></html>`;
};

export const buildStudentReportCardHtml = (
  row: AwardStudentRow,
  displaySubjects: PrintSubject[],
  groups: SubjectGroup[],
  excludedIds: number[],
  className: string,
  section: string | null,
  examLabel: string,
  schoolName: string | undefined,
  schoolLogoUrl: string | undefined,
  displayOptions: PrintDisplayOptions,
): string => {
  const { showPercentage, calculatePercentage, showGrade, colorMode } = displayOptions;
  const p = getPalette(colorMode);
  const extraCols = (showPercentage ? 1 : 0) + (showGrade ? 1 : 0);
  const now = fmtDateTime();
  const initials = getInitials(row.student_name);

  const subs = applyExclusionsToRowSubjects(applyGroupsToRowSubjects(row.subjects, groups), excludedIds);
  const subMap = new Map(subs.map((s) => [s.subject_id, s]));

  const avatarHtml = row.student_profile_picture
    ? `<img src="${row.student_profile_picture}" width="80" height="80" style="border-radius:16px;object-fit:cover;border:3px solid ${p.chipBorder.replace('1px', '3px')};" />`
    : `<div style="width:80px;height:80px;border-radius:16px;background:${p.chipBg};border:2px solid ${p.chipBorder.replace('1px solid ', '')};display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:900;color:${p.textPrimary};">${initials}</div>`;

  const { obt: totalObt, max: totalMax, pct: overallPct } = sumRowMarks(subs);
  const overallGrade = gradeFromPercent(overallPct);
  const gc = printPercentColor(overallPct);
  const passed = overallPct !== null && overallPct >= 33;
  const status = statusBadge(passed, colorMode);

  const subjectRows = displaySubjects.map((sub, si) => {
    const s = subMap.get(sub.subject_id);
    const val = displayValueForSubject(s);
    const pct = subjectPercent(s);
    const rawColor = printPercentColor(pct);
    const color = ink(rawColor, colorMode);
    const hasMarks = !!s?.has_marks;

    // Only draw the percentage progress bar when both "Show %" and
    // "Calculate %" are on — it's a visual percentage indicator, so it
    // must respect the same toggles as the Percentage column itself.
    const bar = (showPercentage && calculatePercentage && pct !== null)
      ? `<div style="width:100%;height:4px;background:#E5E7EB;border-radius:2px;margin-top:5px;overflow:hidden;"><div style="width:${Math.min(pct, 100).toFixed(1)}%;height:4px;background:${color};border-radius:2px;"></div></div>`
      : '';

    return `
      <tr style="background:${si % 2 === 0 ? '#fff' : '#F8FAFF'};">
        <td style="padding:12px 14px;">
          <div style="font-weight:700;color:#0F1B3D;font-size:13px;">${escapeHtml(sub.subject_name)}</div>
          ${bar}
        </td>
        <td style="text-align:center;">${hasMarks ? `<span style="font-weight:800;color:${color};font-size:13px;">${val}</span>` : `<span style="color:#9CA3AF;font-size:12px;">—</span>`}</td>
        ${showPercentage
          ? `<td style="text-align:center;">${calculatePercentage && pct !== null
              ? `<span style="font-weight:700;color:${color};font-size:12px;">${pct.toFixed(1)}%</span>`
              : `<span style="color:#9CA3AF;font-size:12px;">—</span>`}</td>`
          : ''}
        ${showGrade ? `<td style="text-align:center;">${hasMarks ? `<span style="background:${inkBg(rawColor, colorMode)};color:${color};border:1px solid ${inkBorder(rawColor, colorMode)};padding:3px 12px;border-radius:12px;font-size:12px;font-weight:800;">${gradeFromPercent(pct)}</span>` : `<span style="background:#F3F4F6;color:#9CA3AF;border:1px solid #E5E7EB;padding:3px 12px;border-radius:12px;font-size:11px;">N/A</span>`}</td>` : ''}
        <td style="text-align:center;font-size:17px;">${hasMarks ? (pct !== null && pct >= 40 ? '✅' : '❌') : '⏳'}</td>
      </tr>`;
  }).join('');

  const EXTRA = `
.sc{background:${p.bandBg};border:${p.bandBorder};border-radius:16px;padding:24px 26px;margin-bottom:20px;display:flex;align-items:center;gap:20px}
.gc{background:${p.chipBg};border:2px solid ${p.chipBorder.replace('1px solid ', '')};border-radius:14px;padding:14px 20px;text-align:center;min-width:100px}
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}
.sc2{background:#fff;border:1px solid #E5E7EB;border-radius:12px;padding:14px 16px;box-shadow:0 2px 8px rgba(0,0,0,0.05)}
.sk{font-size:9px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:1px}
.sv{font-size:18px;font-weight:800;margin-top:4px}
.sl{font-size:11px;color:#6B7280;margin-top:2px;font-weight:600}
.st{font-size:10px;font-weight:800;color:#6B7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #E5E7EB}`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>Report Card · ${escapeHtml(row.student_name)}</title><style>${getBaseStyles(colorMode)}${EXTRA}</style></head>
<body><div class="page">

  <div class="header" style="margin-bottom:16px;">
    <div class="hl">
      ${buildLogoHtml(schoolName, schoolLogoUrl, colorMode)}
      <div style="font-size:16px;font-weight:800;color:${p.textPrimary};">Student Report Card</div>
      <div style="font-size:11px;color:${p.textSecondary};margin-top:4px;">${examLabel ? examLabel + ' · ' : ''}Generated: ${now}</div>
    </div>
    <div class="hr">
      <div style="font-size:11px;color:${p.textMuted};letter-spacing:.8px;text-transform:uppercase;font-weight:700;margin-bottom:6px;">Academic Record</div>
      <div style="display:inline-block;padding:6px 16px;background:${p.chipBg};border:${p.chipBorder};border-radius:20px;font-size:13px;font-weight:700;color:${p.textPrimary};">Class ${className}${section ? ` · ${section}` : ''}</div>
    </div>
  </div>

  <div class="sc">
    ${avatarHtml}
    <div style="flex:1;">
      <div style="font-size:22px;font-weight:900;color:${p.textPrimary};letter-spacing:-0.5px;line-height:1.2;">${escapeHtml(row.student_name)}</div>
      <div style="font-size:12px;color:${p.textSecondary};margin-top:4px;font-weight:600;">Class ${className}${section ? ` · Section ${section}` : ''} &nbsp;·&nbsp; Roll No. ${row.student_roll_no ?? '—'}</div>
      <div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;">
        <span style="background:${p.chipBg};border:${p.chipBorder};padding:4px 12px;border-radius:20px;font-size:11px;color:${p.textPrimary};font-weight:700;">&#128197; ${now}</span>
        <span style="background:${status.bg};border:${status.border};padding:4px 12px;border-radius:20px;font-size:11px;color:${status.text};font-weight:700;">${passed ? '✅ Pass' : '❌ Fail'}</span>
      </div>
    </div>
    ${(showGrade || showPercentage) ? `<div class="gc">
      ${showGrade ? `<div style="font-size:38px;font-weight:900;color:${p.textPrimary};line-height:1;">${overallGrade}</div>` : ''}
      ${showPercentage ? `<div style="font-size:${showGrade ? '14' : '30'}px;color:${p.textSecondary};margin-top:4px;font-weight:700;">${formatPct(overallPct, calculatePercentage)}</div>` : ''}
      <div style="font-size:9px;color:${p.textMuted};margin-top:2px;letter-spacing:.8px;text-transform:uppercase;font-weight:700;">Overall</div>
    </div>` : ''}
  </div>

  <div class="sg" style="grid-template-columns:repeat(${2 + extraCols},1fr);">
    <div class="sc2"><div class="sk">Total Obtained</div><div class="sv" style="color:${ink('#2855AE', colorMode)};">${totalObt}</div><div class="sl">out of ${totalMax}</div></div>
    ${showPercentage ? `<div class="sc2"><div class="sk">Overall %</div><div class="sv" style="color:${ink(gc, colorMode)};">${formatPct(overallPct, calculatePercentage)}</div><div class="sl">Percentage</div></div>` : ''}
    ${showGrade ? `<div class="sc2"><div class="sk">Grade</div><div class="sv" style="color:${ink(gc, colorMode)};">${overallGrade}</div><div class="sl">Letter grade</div></div>` : ''}
    <div class="sc2"><div class="sk">Subjects</div><div class="sv" style="color:${ink('#0F766E', colorMode)};">${displaySubjects.length}</div><div class="sl">Total</div></div>
  </div>

  <div class="st">Subject-wise Performance</div>
  <div class="table-wrap">
    <table>
      <thead><tr>
        <th>Subject</th>
        <th class="c">Marks</th>
        ${showPercentage ? `<th class="c">Percentage</th>` : ''}
        ${showGrade ? `<th class="c">Grade</th>` : ''}
        <th class="c">Result</th>
      </tr></thead>
      <tbody>
        ${subjectRows || `<tr><td colspan="${3 + extraCols}" style="text-align:center;padding:40px;color:#94A3B8;">No marks recorded</td></tr>`}
      </tbody>
    </table>
  </div>

  <div style="display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:1px solid #E5E7EB;margin-top:4px;">
    <div style="font-size:11px;color:#9CA3AF;"><span style="font-weight:700;color:#2855AE;">${schoolName ? escapeHtml(schoolName) : 'SkoolPillar ERP'}</span> &nbsp;·&nbsp; ${schoolName ? 'Powered by SkoolPillar ERP' : 'School Management System'}</div>
    <div style="font-size:11px;color:#9CA3AF;">${now}</div>
  </div>

</div></body></html>`;
};